// src/common/ui/status-filter.js
// Result-status filter driven by the clickable counts in the document stats bar.
// Shared by the Desktop App and the Word Add-in (the Web App has its own tabs).

import { calculateCitationScore, quoteIssueLabel } from './badges-status.js';
import '../status-filter.css';

/** Filter keys: the three main categories plus the two secondary flags. */
export const STATUS_FILTERS = {
  verified: { label: 'Verified', icon: '✓' },
  issues: { label: 'Issues', icon: '✕' },
  unresolved: { label: 'Unresolved', icon: '?' },
  hallucination: { label: 'Hallucinations', icon: '⚠' },
  quote: { label: 'Quote mismatches', icon: '❝' },
};

let activeFilter = null;
let applyFilterFn = null;
let applyScheduled = false;

const normLabel = (v) => String(v || '').toLowerCase().replace(/-/g, '_');

// Same label chain as calculateCitationScore, so the flags agree with the categories.
const resultLabel = (data) => normLabel(
  data?.label
  || data?.congruence_result?.label
  || data?.analyze_result?.label
  || data?.analyze_result?.congruence_result?.label
);

/**
 * Classify one verdict (a citation result or a joint-group result).
 * @returns {{category: string, hallucination: boolean, quote: boolean}}
 */
export function classifyResult(result) {
  const label = resultLabel(result);
  return {
    category: calculateCitationScore(result).category,
    hallucination: label === 'hallucination',
    quote: !!quoteIssueLabel(result) || ['quote_mismatch', 'quote_not_found', 'quote_altered'].includes(label),
  };
}

function matchesKey(classification, key) {
  if (!classification) return false;
  if (key === 'hallucination') return classification.hallucination;
  if (key === 'quote') return classification.quote;
  return classification.category === key;
}

/**
 * The verdict that decides a citation's status: joint-group members are judged by
 * their group (which is also how the stats bar counts them).
 */
function verdictFor(result, jointGroups) {
  if (!result) return null;
  const groupId = result.joint_evaluation_group;
  if (!groupId && !result.joint_member_result) return result;
  return jointGroups?.[groupId]
    || result.joint_group_result
    || result.congruence?.joint_group_result
    || result.congruence_result?.joint_group_result
    || null;
}

/**
 * Count the secondary flags across the whole results cache, iterating exactly like
 * ResultsCache.calculateDetailedSummary (joint members skipped, groups counted once).
 */
export function countStatusFlags(resultsCache) {
  const counts = { hallucination: 0, quote: 0 };
  if (!resultsCache?.cache) return counts;
  const tally = (result) => {
    const c = classifyResult(result);
    if (c.hallucination) counts.hallucination++;
    if (c.quote) counts.quote++;
  };
  for (const [footnoteId, footnoteResults] of resultsCache.cache.entries()) {
    for (const [citationId, result] of Object.entries(footnoteResults || {})) {
      if (citationId === '__error__' || !result) continue;
      if (result.joint_member_result || result.joint_evaluation_group) continue;
      tally(result);
    }
    for (const group of Object.values(resultsCache.getJointResults(footnoteId))) tally(group);
  }
  return counts;
}

/** Does this citation's result match the active status filter? (true when no filter) */
export function citationMatchesStatusFilter(result, jointGroups) {
  if (!activeFilter) return true;
  const verdict = verdictFor(result, jointGroups);
  return !!verdict && matchesKey(classifyResult(verdict), activeFilter);
}

export function getStatusFilter() {
  return activeFilter;
}

/** Set (or clear, with null) the active filter and re-apply visibility. */
export function setStatusFilter(key) {
  const next = key && STATUS_FILTERS[key] ? key : null;
  if (next === activeFilter) return;
  activeFilter = next;
  syncStatusFilterChips();
  applyFilterFn?.();
}

export function toggleStatusFilter(key) {
  setStatusFilter(activeFilter === key ? null : key);
}

/** Register the function that applies card/block visibility (FootnotesManager). */
export function setStatusFilterApplier(fn) {
  applyFilterFn = fn;
}

/**
 * Re-apply the filter after results change (a citation may have changed category,
 * or new result blocks were rendered). Coalesced to one pass per frame.
 */
export function notifyStatusResultsChanged() {
  if (!activeFilter || !applyFilterFn || applyScheduled) return;
  applyScheduled = true;
  const run = () => { applyScheduled = false; applyFilterFn?.(); };
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else setTimeout(run, 0);
}

// Re-render the chips in place from the counts the stats bar last rendered with.
function syncStatusFilterChips() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.document-stats').forEach((el) => {
    el.classList.toggle('has-status-filter', !!activeFilter);
    const countsEl = el.querySelector('.stats-counts');
    if (countsEl && el._statusCounts) countsEl.innerHTML = renderStatusFilterChips(el._statusCounts);
  });
}

/**
 * Build the clickable count chips for the document stats bar.
 * A chip is shown when its count is non-zero, or when it is the active filter
 * (so the user can always switch an emptied filter off).
 */
export function renderStatusFilterChips(counts) {
  const chips = Object.entries(STATUS_FILTERS).map(([key, def]) => {
    const count = counts[key] || 0;
    const isActive = activeFilter === key;
    if (count === 0 && !isActive) return '';
    const title = isActive
      ? `Showing only ${def.label.toLowerCase()} — click to show all`
      : `Show only ${def.label.toLowerCase()} (${count})`;
    return `<button type="button" class="stats-count stats-filter-chip ${key}${isActive ? ' is-active' : ''}"
      data-status-filter="${key}" aria-pressed="${isActive}" title="${title}">${def.icon}${count}</button>`;
  }).join('');
  const clear = activeFilter
    ? `<button type="button" class="stats-filter-clear" title="Show all citations">Show all</button>`
    : '';
  return chips + clear;
}

/** Wire chip clicks on a stats element (idempotent: one delegated listener). */
export function bindStatusFilterChips(statsEl) {
  if (!statsEl || statsEl.dataset.statusFilterBound) return;
  statsEl.dataset.statusFilterBound = '1';
  statsEl.addEventListener('click', (e) => {
    const chip = e.target.closest('.stats-filter-chip');
    if (chip) {
      e.stopPropagation();
      toggleStatusFilter(chip.dataset.statusFilter);
      return;
    }
    if (e.target.closest('.stats-filter-clear')) {
      e.stopPropagation();
      setStatusFilter(null);
    }
  });
}
